import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SiteLogo, { LOGO_ALT, NAV_BRAND_TITLE } from "./SiteLogo";

const MOBILE_MENU_ID = "mobileMenu";

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Scrolls to a home-page section. Returns false if the section is not in the DOM (not on the home page). */
export function scrollToSection(id, behavior) {
  const target = document.getElementById(id);
  if (!target) return false;
  target.scrollIntoView({ behavior: behavior || (prefersReducedMotion() ? "auto" : "smooth"), block: "start" });
  return true;
}

/**
 * Site-wide navigation bar + mobile menu.
 * `variant="home"` renders the fixed, translucent bar used over the hero;
 * `variant="page"` renders the sticky bar used by /collection and product pages.
 */
export default function SiteNav({ variant = "page" }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const hamburgerRef = useRef(null);
  const firstLinkRef = useRef(null);
  const isHome = location.pathname === "/";

  const close = useCallback(() => setOpen(false), []);

  // Close the menu on route change (e.g. after tapping "Collection").
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        hamburgerRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    // The panel is `inert` until open, so wait for the attribute to clear before focusing.
    const raf = requestAnimationFrame(() => firstLinkRef.current?.focus());
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
      cancelAnimationFrame(raf);
    };
  }, [open]);

  const sectionHref = (id) => (isHome ? `#${id}` : `/#${id}`);

  const onSectionClick = (id) => (e) => {
    e.preventDefault();
    close();
    if (isHome) {
      scrollToSection(id);
    } else {
      // Client-side navigation home; HomePage scrolls to the hash once its content exists.
      navigate(`/#${id}`);
    }
  };

  const sectionLink = (id, label, ref) => (
    <a href={sectionHref(id)} onClick={onSectionClick(id)} ref={ref}>
      {label}
    </a>
  );

  const onHomeClick = (e) => {
    close();
    if (isHome) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? "auto" : "smooth" });
    }
  };

  const navClass = variant === "home" ? undefined : "collection-page__nav";

  return (
    <>
      <div className={`mobile-menu${open ? " open" : ""}`} id={MOBILE_MENU_ID} inert={!open} aria-label="Site menu">
        <button type="button" className="menu-close" onClick={() => { close(); hamburgerRef.current?.focus(); }} aria-label="Close menu">
          ✕
        </button>
        {sectionLink("about", "About", firstLinkRef)}
        <Link to="/collection" onClick={close}>
          Collection
        </Link>
        {sectionLink("process", "How It Works")}
        {sectionLink("testimonials", "Reviews")}
        <a href={sectionHref("custom")} data-custom-order="true" onClick={close}>
          Custom Orders
        </a>
      </div>

      <nav id="navbar" className={navClass} aria-label="Primary">
        <Link to="/" className="nav-brand" aria-label={`${LOGO_ALT} — Home`} onClick={onHomeClick}>
          <SiteLogo variant="nav" />
        </Link>
        <span className="nav-brand-title" aria-hidden="true">
          {NAV_BRAND_TITLE}
        </span>
        <ul className="nav-links">
          {isHome ? (
            <>
              <li>{sectionLink("about", "About")}</li>
              <li>
                <Link to="/collection">Collection</Link>
              </li>
              <li>{sectionLink("process", "Process")}</li>
              <li>{sectionLink("testimonials", "Reviews")}</li>
            </>
          ) : (
            <>
              <li>
                <Link to="/" onClick={onHomeClick}>
                  Home
                </Link>
              </li>
              <li>
                <Link to="/collection">Collection</Link>
              </li>
              <li>{sectionLink("about", "About")}</li>
              <li>{sectionLink("testimonials", "Reviews")}</li>
            </>
          )}
        </ul>
        <a href={sectionHref("custom")} className="nav-cta" data-custom-order="true">
          Custom Order
        </a>
        <button
          type="button"
          className="hamburger"
          ref={hamburgerRef}
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls={MOBILE_MENU_ID}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </nav>
    </>
  );
}
